/**
 * Standardized Company Stage options for Entrepreneur Profiles
 * Designed to be accessible to non-technical founders while remaining credible to startup entrepreneurs
 * 
 * Note: Info text is structured for admin updates without schema changes
 */

export interface CompanyStageOption {
  value: string;
  label: string;
  description: string;
}

export const COMPANY_STAGE_OPTIONS: CompanyStageOption[] = [
  {
    value: "idea_concept",
    label: "Idea / Concept",
    description: "You have an idea for a product or business, but you have not yet built anything or tested it with customers.",
  },
  {
    value: "early_validation",
    label: "Early Validation",
    description: "You are researching the problem, talking to potential customers, or testing assumptions, but you do not yet have a product.",
  },
  {
    value: "prototype",
    label: "Prototype",
    description: "You have built a basic version or proof of concept to demonstrate how the product or service would work, but it may not yet be usable by customers.",
  },
  {
    value: "mvp",
    label: "Minimum Viable Product (MVP)",
    description: "You have a working version of your product or service with just enough features to be used by early customers and to learn what works and what doesn't. An MVP is a functional product, not just an idea or mockup.",
  },
  {
    value: "early_customers",
    label: "Early Customers / Revenue",
    description: "You have active users or paying customers, but the business is still early and evolving.",
  },
  {
    value: "seed_stage",
    label: "Seed Stage Startup",
    description: "You have early traction and are focused on refining the product, growing users or revenue, and possibly raising early-stage funding.",
  },
  {
    value: "growth_stage",
    label: "Growth Stage Startup",
    description: "You have a validated product and are focused on scaling operations, team, and market reach.",
  },
  {
    value: "established_business",
    label: "Established Small Business",
    description: "You operate an ongoing business with consistent customers and revenue. Growth may be steady rather than rapid.",
  },
  {
    value: "scaling_business",
    label: "Scaling Business",
    description: "You are expanding significantly by adding new markets, locations, products, or staff.",
  },
  {
    value: "other",
    label: "Other / Not Sure",
    description: "You're unsure which option fits best or your business doesn't neatly match the stages above.",
  },
];

/**
 * Map of value to label for quick lookups
 */
export const COMPANY_STAGE_LABELS: Record<string, string> = {
  // New standardized values
  idea_concept: "Idea / Concept",
  early_validation: "Early Validation",
  prototype: "Prototype",
  mvp: "Minimum Viable Product (MVP)",
  early_customers: "Early Customers / Revenue",
  seed_stage: "Seed Stage Startup",
  growth_stage: "Growth Stage Startup",
  established_business: "Established Small Business",
  scaling_business: "Scaling Business",
  other: "Other / Not Sure",
  
  // Legacy value mappings (for backward compatibility)
  idea: "Idea / Concept (Legacy)",
  seed: "Seed Stage Startup (Legacy)",
  series_a: "Growth Stage Startup (Legacy)",
  growth: "Growth Stage Startup (Legacy)",
};

/**
 * Mapping from legacy values to new standardized values
 */
export const LEGACY_STAGE_MAPPING: Record<string, string> = {
  idea: "idea_concept",
  prototype: "prototype", // Same value, no change needed
  mvp: "mvp", // Same value
  seed: "seed_stage",
  series_a: "growth_stage",
  growth: "growth_stage",
};

/**
 * Get the display label for a company stage value
 * Handles both new and legacy values
 */
export function getCompanyStageLabel(value: string | null | undefined): string {
  if (!value) return "";
  
  // Check if it's a known value (new or legacy)
  if (COMPANY_STAGE_LABELS[value]) {
    return COMPANY_STAGE_LABELS[value].replace(" (Legacy)", "");
  }
  
  // Unknown value - return as-is with title case
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Get the standardized value for a legacy stage value
 */
export function getStandardizedStageValue(value: string | null | undefined): string | null {
  if (!value) return null;
  
  // If it's already a new standardized value, return as-is
  if (COMPANY_STAGE_OPTIONS.some((opt) => opt.value === value)) {
    return value;
  }
  
  // Try to map from legacy value
  if (LEGACY_STAGE_MAPPING[value]) {
    return LEGACY_STAGE_MAPPING[value];
  }
  
  // Unknown value - map to "other"
  return "other";
}

/**
 * Get the description for a company stage value
 */
export function getCompanyStageDescription(value: string | null | undefined): string {
  if (!value) return "";
  
  const option = COMPANY_STAGE_OPTIONS.find((opt) => opt.value === value);
  return option?.description || "";
}

/**
 * Helper text for the Company Stage field
 */
export const COMPANY_STAGE_HELPER_TEXT = 
  "Choose the stage that best describes where your business is today. This helps mentors understand your needs and provide appropriate guidance.";

/**
 * MVP explanation tooltip text
 */
export const MVP_EXPLANATION = 
  "An MVP is a functional product designed to test real customer behavior, not just an idea or mockup.";
